import { GENDER_OPTIONS } from '$app/constants/common'
import { pluralize } from '$app/utils'
import FooterAction from '$components/FooterAction'
import Loading from '$components/Loading'
import Select from '$components/Select'
import {
  useRedirectPrompt,
  useRouteSummary
} from '$contexts/RouteContext/hooks'
import {
  useAppUserService,
  useGlobalAppUserService,
  usePasswordSettingValues
} from '$hooks/services'
import {
  DEFAULT_FORM_VALUES,
  schema,
  TFormValues
} from '$pages/Apps/AppUser/constants'
import AssignRoles from '$blocks/AssignRoles'
import { flexColumn, flexRow } from '$styles/common.css'
import { Banner, Button, Spinner } from '@genie-fintech/ui/components'
import { BaseText, BaseTextarea } from '@genie-fintech/ui/components/fields'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { zodResolver } from '@hookform/resolvers/zod'
import { useBoolean, useToggle } from 'ahooks'
import { ArrowRight, ChevronLeft } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Controller,
  useController,
  useFieldArray,
  useForm,
  useWatch
} from 'react-hook-form'
import { TSavedUserInfo } from '../types'
import UpdatePasswordBlock from '$blocks/UpdatePassword'
import { usePasswordPolicyStore } from '$hooks/stores/usePasswordPolicyStore'
import { toast } from 'sonner'
import LoginUserType from '$components/LoginUserType'
import { SERVICE_USER_TYPE } from '$services/api'
import { footnote, title } from '@genie-fintech/ui/style/typography'
import { modalContentBlock, modalContentCard } from '../styles.css'
import {
  cardInner,
  loginUserTypeInfoContainer,
  loginUserTypeInfoContainerGrid,
  loginUserTypeInfoContainerGridItemLabel,
  loginUserTypeInfoContainerGridItemValue,
  loginUserTypeInfoContainerGridTitle,
  userInfoFormContainer
} from './styles.css'
import Phone from '$components/Phone'
import { COMPANY_NAME } from '$constants/themeTexts'

const { colors } = themeVars

interface IProps {
  userId: string
  email: string
  onQueryAgain: VoidFunction
  onSuccess: (value: TSavedUserInfo) => void
}

const UserForm = ({ userId, email, onQueryAgain, onSuccess }: IProps) => {
  const [step, setStep] = useState(1)

  const [password, setPassword] = useState('')

  const [hasError, { setTrue, setFalse }] = useBoolean()

  const [isNextSignIn, { toggle }] = useToggle(true)

  const {
    route: { params }
  } = useRouteSummary()

  const { appId } = params

  const { fetchGlobalAppUserDetailAsync, fetchingGlobalAppUserDetail } =
    useGlobalAppUserService()

  const {
    saveUserAsync,
    savingUser,
    attachRoleToUserAsync,
    attachingRoleToUser
  } = useAppUserService()

  const methods = useForm<TFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_FORM_VALUES
  })

  const {
    fetchDefaultPasswordSettingValuesAsync,
    defaultPasswordSettingValues
  } = usePasswordSettingValues()

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isDirty, errors },
    getValues
  } = methods

  const { replace } = useFieldArray<TFormValues>({
    name: 'roles' as never,
    control
  })

  const { field: phoneCodeField } = useController({
    name: 'phone_code',
    control
  })
  const { field: phoneNoField } = useController({
    name: 'phone_no',
    control
  })

  const selectedRoles = useWatch({ name: 'roles', control })

  const name = useWatch({ name: 'name', control })

  const passwordRules = usePasswordPolicyStore(state => state.passwordRules)

  const { field: userTypeField } = useController({ name: 'user_type', control })

  useRedirectPrompt({ shouldPrompt: isDirty })

  useEffect(() => {
    if (!userId) {
      fetchDefaultPasswordSettingValuesAsync()
      reset({ ...DEFAULT_FORM_VALUES, email })
      return
    }

    fetchGlobalAppUserDetailAsync(userId).then(({ data }) => {
      const { gender, phone_code, phone_no, date_of_birth, address, ...rest } =
        data

      const modifiedData: TFormValues = {
        roles: [],
        gender: GENDER_OPTIONS.find(d => d.value === gender) ?? {
          label: '',
          value: ''
        },
        address: address ?? '',
        date_of_birth: date_of_birth ?? '',
        phone_code: phone_code ?? '',
        phone_no: phone_no ?? '',
        ...rest
      }

      reset(modifiedData)
    })
  }, [
    userId,
    email,
    fetchGlobalAppUserDetailAsync,
    reset,
    fetchDefaultPasswordSettingValuesAsync,
    getValues
  ])

  const onChangePassword = useCallback(
    (pwd: string) => {
      setPassword(pwd)
      setFalse()
    },
    [setFalse]
  )

  const onBack = useCallback(() => {
    if (step === 1) {
      return onQueryAgain()
    }
    setStep(prev => prev - 1)
  }, [step, onQueryAgain])

  const isPwdValid = useMemo(
    () =>
      userId
        ? true
        : passwordRules.every(d => !!password && d.regex.test(password)),
    [userId, password, passwordRules]
  )

  const onNext = useCallback(async () => {
    const isValid = await trigger(['name', 'email'])

    if (!isPwdValid) {
      toast.error('Password must be valid with all password policies')
      setTrue()
      return
    }

    if (isValid && isPwdValid) setStep(prev => prev + 1)
  }, [trigger, isPwdValid, setTrue])

  const onSubmit = handleSubmit(
    ({ gender, roles, user_type, phone_code, ...rest }) => {
      if (!appId) return

      if (userId) {
        attachRoleToUserAsync(appId, userId, roles).then(() => {
          onSuccess({ name: name, password, id: userId })
        })
        return
      }

      saveUserAsync(appId, {
        password,
        gender: gender.value,
        phone_code: rest.phone_no ? phone_code : '',
        roles,
        should_reset_password: isNextSignIn,
        is_internal: user_type === SERVICE_USER_TYPE.INTERNAL,
        ...rest
      }).then(({ data }) => {
        onSuccess({ name: name, password, id: data.id })
        reset()
      })
    }
  )

  const {
    access_token_expires_in,
    password_expiration_days,
    password_history_depth,
    mfa_enabled
  } =
    defaultPasswordSettingValues.find(d => d.type === userTypeField.value) ?? {}

  const isProcessing = savingUser || attachingRoleToUser

  if (fetchingGlobalAppUserDetail) return <Loading />

  return (
    <form
      onSubmit={onSubmit}
      className={flexColumn}
      style={{ maxHeight: '100vh', overflowY: 'auto', paddingBottom: 60 }}
    >
      <article className={modalContentBlock}>
        <div>
          <Button
            styleVariants={{ kind: 'neutral', type: 'outlined', size: 'small' }}
            style={{ background: colors.area.high }}
            onClick={onBack}
            disabled={isProcessing}
          >
            <ChevronLeft size={16} />
            Back
          </Button>
        </div>

        <article
          className={modalContentCard}
          style={{ padding: 0, gap: 0 }}
          hidden={step !== 1}
        >
          <article className={cardInner}>
            <Banner
              border
              kind="success"
              type="transparent"
              title={
                userId
                  ? `This user is already in the ${COMPANY_NAME} system.`
                  : `This user is a new user for ${COMPANY_NAME} system.`
              }
              suffix={
                <Button
                  onClick={onQueryAgain}
                  styleVariants={{ kind: 'success', size: 'small' }}
                >
                  Change email or query again
                  <ArrowRight size={16} />
                </Button>
              }
            />

            <article className={userInfoFormContainer}>
              <article className={flexColumn} style={{ gap: 4 }}>
                <p className={title.two}>USER INFO</p>
                <p
                  className={footnote.one}
                  style={{ color: colors.neutral[60] }}
                >
                  Access detailed information about each user to manage their
                  settings and permissions.
                </p>
              </article>

              <article className={flexColumn} style={{ gap: 20 }}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <BaseText
                        label="Name"
                        required
                        disabled={!!userId}
                        error={!!error?.message}
                        message={error?.message}
                        inputProps={{
                          ...field,
                          type: 'text',
                          disabled: !!userId
                        }}
                      />
                    )
                  }}
                />

                <Controller
                  name="date_of_birth"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <BaseText
                        label="Date of Birth"
                        disabled={!!userId}
                        error={!!error?.message}
                        message={error?.message}
                        inputProps={{
                          ...field,
                          type: 'date',
                          disabled: !!userId
                        }}
                      />
                    )
                  }}
                />

                <Controller
                  name="gender"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <article className="flex flex-col gap-y-1">
                        <label className="text-sm font-medium">Gender</label>
                        <Select
                          {...field}
                          options={GENDER_OPTIONS}
                          error={!!error?.message}
                          disabled={!!userId}
                        />
                        {error?.message && (
                          <p className="text-xs text-[--colors-danger-default]">
                            {error.message}
                          </p>
                        )}
                      </article>
                    )
                  }}
                />

                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => {
                    return (
                      <article className="flex flex-col gap-1">
                        <BaseText
                          label="Email"
                          required
                          disabled
                          inputProps={{
                            value: field.value,
                            readOnly: true,
                            type: 'email',
                            disabled: !!userId
                          }}
                        />

                        <article className="flex justify-end">
                          <Button
                            styleVariants={{ type: 'text' }}
                            onClick={onQueryAgain}
                          >
                            Change Email
                            <ArrowRight size={16} />
                          </Button>
                        </article>
                      </article>
                    )
                  }}
                />

                <Phone
                  label="Phone Number"
                  inputProps={phoneNoField}
                  callingCodeProps={phoneCodeField}
                />

                <Controller
                  name="address"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <BaseTextarea
                        textareaProps={{ ...field }}
                        label="Address"
                        error={!!error?.message}
                        message={error?.message}
                        disabled={!!userId}
                      />
                    )
                  }}
                />
              </article>
            </article>
          </article>

          <article
            className={flexColumn}
            style={{ borderBottom: `1px solid ${colors.neutral[10]}` }}
          >
            <LoginUserType
              value={userTypeField.value as SERVICE_USER_TYPE}
              onChange={userId ? undefined : userTypeField.onChange}
            />

            {!userId && (
              <article
                className={flexColumn}
                style={{ padding: 32, paddingTop: 0 }}
              >
                <article className={loginUserTypeInfoContainer}>
                  <h3 className={loginUserTypeInfoContainerGridTitle}>
                    DEFAULT RULES FOR {userTypeField.value} USERS
                  </h3>

                  <article className={loginUserTypeInfoContainerGrid}>
                    <article className={flexColumn} style={{ gap: 2 }}>
                      <p className={loginUserTypeInfoContainerGridItemLabel}>
                        Password Expiry Every
                      </p>
                      <p className={loginUserTypeInfoContainerGridItemValue}>
                        {password_expiration_days
                          ? pluralize(password_expiration_days, 'day')
                          : 'No Expired'}
                      </p>
                    </article>

                    <article className={flexColumn} style={{ gap: 2 }}>
                      <p className={loginUserTypeInfoContainerGridItemLabel}>
                        Daily Login Required
                      </p>
                      <p className={loginUserTypeInfoContainerGridItemValue}>
                        {access_token_expires_in
                          ? pluralize(access_token_expires_in, 'day')
                          : 'No Required'}
                      </p>
                    </article>

                    <article className={flexColumn} style={{ gap: 2 }}>
                      <p className={loginUserTypeInfoContainerGridItemLabel}>
                        Previous Passwords can not be Reused
                      </p>
                      <p className={loginUserTypeInfoContainerGridItemValue}>
                        {pluralize(password_history_depth, 'Password')}
                      </p>
                    </article>

                    <article className={flexColumn} style={{ gap: 2 }}>
                      <p className={loginUserTypeInfoContainerGridItemLabel}>
                        Two-factor Authentication
                      </p>
                      <p className={loginUserTypeInfoContainerGridItemValue}>
                        {mfa_enabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </article>
                  </article>
                </article>
              </article>
            )}
          </article>

          {!userId && (
            <UpdatePasswordBlock
              onChangePwd={onChangePassword}
              pwd={password}
              nextSignIn={isNextSignIn}
              onChangeNextSignIn={toggle}
              error={hasError}
            />
          )}
        </article>

        <article hidden={step !== 2}>
          <AssignRoles
            roles={selectedRoles}
            onChange={replace}
            errorMessage={errors.roles?.message}
            appId={appId}
          />
        </article>
      </article>

      {step < 3 && (
        <FooterAction fullWidth>
          <article className={modalContentBlock}>
            <article className={flexRow} style={{ justifyContent: 'flex-end' }}>
              {step === 1 && (
                <Button disabled={!isDirty && !userId} onClick={onNext}>
                  Next
                </Button>
              )}

              {step === 2 && (
                <Button type="submit" disabled={isProcessing}>
                  {isProcessing && <Spinner />}
                  Create
                </Button>
              )}
            </article>
          </article>
        </FooterAction>
      )}
    </form>
  )
}

export default UserForm
